import React, { useMemo } from 'react';
import { TypographyH4 } from '@/Components/shadcn';
import { Card, CardContent } from '@/Components/shadcn/card';
import { Button } from '@/Components/shadcn/button';
import { Spinner } from '@/Components/shadcn/spinner';
import UIStrings from '@/Utils/UIStrings';
import {
    useDeleteEmailConnection,
    useEmailIntegrationStatus,
    useGetPersonalEmailSetupUrl,
} from '../../api/useOAuthEmailIntegration';
import GoogleLogo from './GoogleLogo.svg';
import MicrosoftLogo from './MicrosoftLogo.svg';
import StatusInfo from '../StatusInfo';
import toDateString from '@/Utils/DateHelper';

const EmailIntegrationStatus = () => {
    const { data, isValidating } = useEmailIntegrationStatus();
    const { trigger: deleteConnection, isMutating: isDeleting } = useDeleteEmailConnection();
    const { trigger } = useGetPersonalEmailSetupUrl();

    const logo = useMemo(() => {
        switch (data?.provider) {
            case 'google':
                return GoogleLogo;
            case 'microsoft':
            default:
                return MicrosoftLogo;
        }
    }, [data?.provider]);

    const handleDisconnect = async () => {
        await deleteConnection();
    };

    const handleReauthenticate = async () => {
        const { url } = await trigger(data?.provider!);
        window.open(url, '_self');
    };

    return (
        <Card className="w-max rounded-xl border p-4 drop-shadow-none">
            <CardContent className="flex flex-col gap-6 p-0" data-cy="IntegrationDetails">
                <div className="flex flex-row items-center gap-2">
                    <object width="40px" height="40px" data={logo} aria-label="email provider logo" />
                    <TypographyH4 className="self-center text-lg capitalize">{data?.provider}</TypographyH4>
                </div>
                <div className="grid w-auto grid-cols-12 gap-x-8 gap-y-2">
                    <StatusInfo label={UIStrings.Status} textTransform="capitalize">
                        <span
                            className={`mr-1 inline-block h-2 w-2 rounded-full ${data?.connectionStatus === 'Valid' ? 'bg-green-500' : 'bg-red-500'}`}
                            aria-hidden
                        />
                        {` ${data?.connectionStatus}`}
                    </StatusInfo>
                    <StatusInfo label={UIStrings.Email}>{data?.emailAddress}</StatusInfo>
                    <StatusInfo label={UIStrings.LastSynced}>
                        {data?.lastSyncedAt ? toDateString(new Date(data?.lastSyncedAt)) : ''}
                    </StatusInfo>
                </div>
                <div className="flex flex-row gap-2">
                    <Button
                        type="button"
                        variant="destructive-outline"
                        size="sm"
                        disabled={isValidating || isDeleting}
                        onClick={handleDisconnect}
                        className="flex-1"
                    >
                        {(isValidating || isDeleting) && <Spinner className="mr-2 size-4" />}
                        {!isValidating && UIStrings.Disconnect}
                    </Button>
                    {data?.connectionStatus === 'Invalid' && (
                        <Button
                            type="button"
                            variant="default-outline"
                            size="sm"
                            disabled={isDeleting}
                            onClick={handleReauthenticate}
                            className="flex-1"
                        >
                            {isValidating && <Spinner className="mr-2 size-4" />}
                            {UIStrings.Reauthenticate}
                        </Button>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

export default EmailIntegrationStatus;
